import React, { FC, Fragment, ReactNode } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import { FormattedMessage } from 'react-intl';
import { SeoLink } from 'dibs-seo/exports/SeoLink';
import { QuickViewCreators_item } from './__generated__/QuickViewCreators_item.graphql';
import { QuickViewCreators_itemSearch } from './__generated__/QuickViewCreators_itemSearch.graphql';
import { PAGE_TYPE } from 'dibs-constants/exports/pageTypes';
import { CREATOR_UNKNOWN } from '../constants';

type QuickViewCreatorsProps = {
    item: QuickViewCreators_item;
    itemSearch?: QuickViewCreators_itemSearch | null;
    className: string;
};

const whiteListedPageTypes = [PAGE_TYPE.BROWSE];
const blackListedPageTypes = [PAGE_TYPE.ORIGIN];

export const copy = {
    by: (
        <FormattedMessage id="dc.searchProductTile.by" defaultMessage="By">
            {msg => <Fragment>{msg}</Fragment>}
        </FormattedMessage>
    ),
};

const QuickViewCreators: FC<QuickViewCreatorsProps> = ({ item, itemSearch, className }) => {
    const creatorsList = item.quickViewDisplay?.creators;

    if (!creatorsList || !creatorsList.length) {
        return null;
    }

    const pageType = itemSearch?.pageType;
    const isWhiteListed = pageType && whiteListedPageTypes.includes(pageType);
    const isBlackListed = pageType && blackListedPageTypes.includes(pageType);
    const showLinks = (itemSearch?.isAttributePage && !isBlackListed) || isWhiteListed;

    const creators = creatorsList.map<ReactNode>(creator => {
        if (!creator || creator.displayName === CREATOR_UNKNOWN) {
            return null;
        }

        return showLinks ? (
            <SeoLink
                key={creator.displayName}
                linkData={creator.linkData}
                dataTn="quick-view-creator-link"
            >
                {creator.displayName}
            </SeoLink>
        ) : (
            <Fragment key={creator.displayName}>{creator.displayName}</Fragment>
        );
    });

    return (
        <div data-tn="quick-view-creators" className={className}>
            {copy.by} {creators.reduce((prev, curr) => [prev, ', ', curr])}
        </div>
    );
};

export default createFragmentContainer(QuickViewCreators, {
    itemSearch: graphql`
        fragment QuickViewCreators_itemSearch on ItemSearchQueryConnection {
            pageType
            isAttributePage
        }
    `,
    item: graphql`
        fragment QuickViewCreators_item on Item {
            quickViewDisplay {
                creators {
                    displayName
                    linkData {
                        ...SeoLink_linkData
                    }
                }
            }
        }
    `,
});
